import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { signInWithEmailAndPassword } from "firebase/auth";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import app, { auth } from "../firebaseConfig";
import { Brain } from "lucide-react";
import { resolveEmployeeStatus } from "../utils/employeeStatus";

export default function LoginPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [status, setStatus] = useState({ loading: false, error: "", success: "" });

  // Check if Firebase is properly initialized
  if (!app || !auth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="bg-white shadow-xl rounded-3xl p-8 w-full max-w-md border border-gray-100">
          <div className="text-center mb-8">
            <div className="flex justify-center mb-3">
              <Brain className="text-red-600 w-12 h-12 mb-2" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Configuration Error</h1>
            <p className="text-gray-500 text-sm mt-1">Unable to initialize Firebase</p>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
            <p className="text-red-700 text-sm">
              The application is not properly configured. Please ensure all environment variables are set correctly.
            </p>
          </div>
          <button
            onClick={() => navigate('/')}
            className="w-full mt-6 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  const firestore = getFirestore(app);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, error: "", success: "" });

    try {
      // 1️⃣ Sign in
      const userCredential = await signInWithEmailAndPassword(auth, form.email, form.password);
      const user = userCredential.user;
      const uid = user.uid;

      // 2️⃣ Fetch role from Firestore "users" collection (note lowercase 'users')
      const userDocRef = doc(firestore, "users", uid);
      const userSnap = await getDoc(userDocRef);

      if (!userSnap.exists()) {
        throw new Error("User record not found in database!");
      }

      const userData = userSnap.data();
      let role = (userData.accountType || userData.role || "unknown").toLowerCase();
      const employeeStatus = resolveEmployeeStatus(userData);


      setStatus({
        loading: false,
        success: `Welcome ${userData.fullName || role}! Redirecting...`,
        error: "",
      });

      // 3️⃣ Redirect based on role
      setTimeout(() => {
        switch (role) {
          case "admin":
            navigate("/admin-dashboard");
            break;
          case "employee":
            if (employeeStatus === "suspended") {
              navigate("/employee-dashboard", { state: { status: "suspended" } });
            } else if (userData.companyName && employeeStatus === "approved") {
              navigate("/company-dashboard");
            } else {
              navigate("/employee-dashboard");
            }
            break;
          case "farmer":
            navigate("/farmer-dashboard");
            break;
          case "company":
            navigate("/company-dashboard");
            break;
          case "client":
            navigate("/client-dashboard");
            break;
          default:
            navigate("/dashboard");
            break;
        }
      }, 1000);
    } catch (error) {
      console.error("Login Error:", error);
      setStatus({
        loading: false,
        success: "",
        error: error.message || "Login failed. Please try again.",
      });
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="bg-white shadow-xl rounded-3xl p-8 w-full max-w-md border border-gray-100">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-3">
            <Brain className="text-blue-600 w-12 h-12 mb-2" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Neuracraft Solutions</h1>
          <p className="text-gray-500 text-sm mt-1">Smart. Secure. Connected.</p>
        </div>

        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Email</label>
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              className="mt-1 w-full px-3 py-2 border rounded-xl focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter your email"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Password</label>
            <input
              type="password"
              name="password"
              value={form.password}
              onChange={handleChange}
              className="mt-1 w-full px-3 py-2 border rounded-xl focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter your password"
              required
            />
          </div>

          {status.error && (
            <div className="bg-red-50 text-red-700 px-4 py-2 rounded-lg text-sm text-center">
              {status.error}
            </div>
          )}

          {status.success && (
            <div className="bg-green-50 text-green-700 px-4 py-2 rounded-lg text-sm text-center">
              {status.success}
            </div>
          )}

          <button
            type="submit"
            disabled={status.loading}
            className={`w-full py-3 font-semibold rounded-xl text-white transition-all ${
              status.loading ? "bg-blue-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"
            }`}
          >
            {status.loading ? "Signing in..." : "Sign In"}
          </button>
        </form>

        <div className="text-center mt-5">
          <a href="/forgot-password" className="text-sm text-blue-600 hover:underline">
            Forgot password?
          </a>
        </div>
        <div className="text-center mt-5">
          <a href="/signup-page" className="text-sm text-blue-600 hover:underline">
            Sign up for an account
          </a>
        </div>
      </div>
    </div>
  );
}
